import { FC } from "react";
import { Check, CornerDownLeft, PackageCheck, Truck } from "lucide-react";
import { MdOutlineIncompleteCircle } from "react-icons/md";
import { useToast } from "../../@/components/ui/use-toast";
import { IsServiceTypeVendor, isMobileScreen } from "../../lib/utils";

interface Step {
  id: number;
  label: string;
  icon: JSX.Element;
}

interface StepperProps {
  perviousStep: number;
  currentStep: number;
  setCurrentStep: (step: number) => void;
  role: string;
  serviceTypeId: number;
}

const OrderStepper: FC<StepperProps> = ({
  perviousStep,
  currentStep,
  setCurrentStep,
  role,
  serviceTypeId,
}) => {
  const { toast } = useToast();
  const isVendorService = IsServiceTypeVendor();

  const allSteps: Step[] = [
    { id: 1, label: "Activated", icon: <Check color="white" /> },
    { id: 2, label: "Confirmed", icon: <PackageCheck color="white" /> },
    { id: 3, label: "Dispatched", icon: <PackageCheck color="white" /> },
    {
      id: 4,
      label: role === "VENDOR" && isVendorService ? "Completed" : "Delivered",
      icon:
        role === "VENDOR" && isVendorService ? (
          <MdOutlineIncompleteCircle color="white" />
        ) : (
          <Truck color="white" />
        ),
    },
    { id: 5, label: "Returned", icon: <CornerDownLeft color="white" /> },
  ];

  const steps =
    role === "VENDOR" && isVendorService
      ? allSteps.filter((step) =>
          ["Activated", "Confirmed", "Completed", "Cancelled"].includes(
            step.label
          )
        )
      : allSteps;

  const handleOnClick = (step: any) => {
    if (perviousStep === 6 || perviousStep === 15) {
      toast({
        title: "Error",
        description: "You can't perform this action after order is updated.",
      });
      return;
    }
    if (step.id === 1) {
      toast({
        title: "Error",
        description: "You can't perform this action after order is updated.",
      });
      return;
    }

    if (role === "VENDOR" && isVendorService) {
      if (currentStep === step.id) {
        const availableSteps = steps.filter((s) => s.id < step.id);
        const previousStep = availableSteps[availableSteps.length - 1];
        setCurrentStep(previousStep?.id || step.id);
      } else {
        setCurrentStep(step.id);
      }
    } else {
      if (currentStep === step.id) {
        setCurrentStep(step.id - 1);
      } else {
        setCurrentStep(step.id);
      }
    }
  };

  return (
    <div className="relative w-full overflow-x-auto">
      <div className="flex items-center justify-between min-w-[600px] px-4">
        {steps.map((step, index) => (
          <div key={step.id} className="flex items-center justify-center">
            <div
              onClick={() => {
                handleOnClick(step);
              }}
              className={`flex items-center mt-2 flex-col cursor-pointer`}
            >
              <div
                className={`flex items-center justify-center w-8 h-8 rounded-full border 
                  ${
                    currentStep >= step.id
                      ? "bg-customGreenColor"
                      : "bg-gray-300"
                  }`}
                style={{ position: "relative" }}
              >
                {step.icon}
              </div>
              {/* Label */}
              <span
                className={`mx-2 text-sm whitespace-nowrap ${
                  currentStep >= step.id
                    ? "text-customGreenColor font-semibold"
                    : "text-gray-500"
                }`}
              >
                {step.label}
              </span>
            </div>

            {/* Line */}
            {index !== steps.length - 1 && (
              <div
                className={`h-0.5 lg:w-[7vw] ${
                  IsServiceTypeVendor()
                    ? isMobileScreen
                      ? "w-[170px]"
                      : "lg:w-[10vw]"
                    : "w-[50px]"
                } ${
                  currentStep > step.id && step.id < 5
                    ? "bg-customGreenColor"
                    : ""
                }`}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default OrderStepper;
